import { useState } from "react";

import { toCount } from "common/utils/formatting";
import { toggleValue } from "common/utils/sets";
import { IAddress } from "common/types";
import CatalogImage from "common/components/CatalogImage";
import { deepCombineErrors } from "common/utils/errors";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { ListSubscription } from "client/subscriptions/types";
import { useUpdateBulkSubscriptionsMutation } from "client/subscriptions/hooks";
import Checkbox from "client/common/components/inputs/Checkbox";
import { useCurrentDrawer } from "client/common/hooks/stackables";

import styles from "../../styles/SubscriptionBulkUpdateAddressDrawer.module.less";

export type SubscriptionsBulkUpdateAddressDrawerProps = {
  subscriptions: ListSubscription[];
  address: IAddress;
};

const SubscriptionBulkUpdateAddressDrawer = ({
  subscriptions,
  address,
}: SubscriptionsBulkUpdateAddressDrawerProps) => {
  const { close } = useCurrentDrawer();
  const {
    mutate: bulkUpdate,
    isPending: isLoading,
    error,
  } = useUpdateBulkSubscriptionsMutation({
    onSuccess: close,
  });
  const [appliedSubscriptions, setAppliedSubscriptions] = useState<Set<number>>(
    new Set(subscriptions.map((subscription) => subscription.id)),
  );

  const onSubscriptionToggled = (subscriptionId: number) => {
    setAppliedSubscriptions(toggleValue(appliedSubscriptions, subscriptionId));
  };
  const onSubmit = () => {
    bulkUpdate({
      shipping_address_id: address.id,
      subscription_ids: [...appliedSubscriptions],
    });
  };

  const modalLayoutHeader = (
    <div className={styles.nameContainer}>
      You have Autoships with another address on file. Select the Autoships you would like to update
      to the new address:
      <div className={styles.nameContainer}>
        <div className={styles.name}>
          <b>{address.address_one}</b>
        </div>
      </div>
    </div>
  );

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Update your Autoships"
        description={modalLayoutHeader}
        onClose={close}
      />
      <DrawerLayout.Form
        canSubmit={true}
        onSubmit={onSubmit}
        submitText={`Update ${toCount(appliedSubscriptions.size, "Autoship")}`}
        isLoading={isLoading}
        error={error}
        fieldErrors={[deepCombineErrors(error?.errors)]}
      >
        {subscriptions.map((subscription) => (
          <DrawerLayout.Form.Group key={subscription.id}>
            <div className={styles.option}>
              <Checkbox
                value={appliedSubscriptions.has(subscription.id)}
                onChange={() => onSubscriptionToggled(subscription.id)}
              />
              <div className={styles.item}>
                <CatalogImage
                  image={subscription.item.image}
                  size="thumbnail"
                  alt={subscription.item.full_name}
                  className={styles.image}
                />
                <div className={styles.nameContainer}>
                  <div className={styles.name}>
                    {subscription.item.product.product_line.name} {subscription.item.product.name}{" "}
                    for <b>{subscription.patient.name_display}</b>
                  </div>
                  {subscription.shipping_address && (
                    <div className={styles.name}>
                      Currently ships to:
                      <div className={styles.name}>
                        <b>{subscription.shipping_address.address_one}</b>
                      </div>
                      {!!subscription.shipping_address.address_two && (
                        <div className={styles.name}>
                          <b>{subscription.shipping_address.address_two}</b>
                        </div>
                      )}
                      <div className={styles.name}>
                        <b>
                          {subscription.shipping_address.city},{" "}
                          {subscription.shipping_address.state}{" "}
                          {subscription.shipping_address.zipcode}
                        </b>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </DrawerLayout.Form.Group>
        ))}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default SubscriptionBulkUpdateAddressDrawer;
